import { createContext, FC, useContext } from "react";
import { SimpleOption2 } from "../governance/search/AmbientFacilitiesContext";

export interface RuleDisplayTree
{
  Kind: RuleDisplayKind;
  Left?: RuleDisplayTree;
  Right?: RuleDisplayTree;
  Group?: RuleDisplayTree[];
  Value?: number|string;
  Values?: (number|string)[];
}

export type RuleDisplayKind =
  | 'PickValue'
  | 'PickConstant'
  | 'PickConstants'
  | 'GroupAnd'
  | 'GroupOr'
  | 'Equals'
  | 'Contains'
  | 'Matches'
  | 'EqualsAny'
  | 'MatchesAny'
  | 'LogicNot'
  | 'Noop'
  ;
  
export const pickerRules = new Set<RuleDisplayKind>([
  'PickValue',
  'PickConstant',
  'PickConstants',
])
  
export const groupRules = new Set<RuleDisplayKind>([
  'GroupAnd',
  'GroupOr',
])

export const operatorRules = new Set<RuleDisplayKind>([
  'Equals',
  'Contains',
  'Matches',
  'EqualsAny',
  'MatchesAny',
])


export const opOptions: SimpleOption2[] = Object.freeze([
  { key: "Equals", text: "Equals", flipText: 'Not Equals' },
  { key: "Contains", text: "Contains", flipText: 'Not Contains' },
  { key: "Matches", text: "Matches", flipText: 'Not Matches' },
  { key: "EqualsAny", text: "Equals Any", flipText: 'Equals None Of' },
  { key: "MatchesAny", text: "Matches Any", flipText: 'Matches None Of' },
].map(x => Object.freeze(x))) as any

export interface OperatorOptionExplainationMap {
  [K: string]: {[k in 'Equals'|'Contains'|'Matches'|'EqualsAny'|'MatchesAny']?: string}
}

export const generalOpOptionExplainations = Object.freeze({
  general: {
    Equals: 'Exact string comparison with a single value in the input.',
    Contains: 'Substring comparison with a single value in the input.',
    Matches: 'String match with a single regular expression in the input.',
    EqualsAny: 'Exact string comparison with one or more values in the input (any one match is a hit).',
    MatchesAny: 'String match with one or more regular expressions in the input (any one match is a hit).',
  },
}) as OperatorOptionExplainationMap


export interface PickerOptionAmbientDataContextProps {
  [K: string]: string[];
}

export interface PickValueOption extends SimpleOption2 {
  explain: string;
}

export interface OpRuleStates {
  preRule: RuleDisplayTree;
  multipleValues: boolean;
  composedRule: RuleDisplayTree;
  isValid: boolean;
  errMsg: string | undefined;
  showRightExample: boolean;
}

export interface RuleDisplayPresetProps {
  defaults: {
    rule: () => RuleDisplayTree;
    opLeft: () => RuleDisplayTree["Left"];
    opRight: () => RuleDisplayTree["Right"];
  },
  leftPickeValueOptions: PickValueOption[],
  pickerAdjust: {
    adjustOpKind: (r: RuleDisplayTree, v: OpRuleStates) => RuleDisplayKind | undefined,
    adjustOpOptions: (r: RuleDisplayTree) => SimpleOption2[] | undefined,
  },
  rightPickerOptions: {
    useDataSource: () => PickerOptionAmbientDataContextProps,
    getPickerOptionProps: (r: RuleDisplayTree) => {
      optionSource: string;
      multiple: boolean;
    }|undefined,
  },
  explaination: {
    getOperatorOptionExplainationMap: (r: RuleDisplayTree) => OperatorOptionExplainationMap,
    generateRightExample: (composedRule: RuleDisplayTree) => any,
  },
}

const RuleDisplayPresetContext = createContext<RuleDisplayPresetProps>({} as any)


export const RuleDisplayPresetContextProvider : FC<{value: RuleDisplayPresetProps}> = ({value, children}) => {
  return (
    <RuleDisplayPresetContext.Provider value={value}>
      {children}
    </RuleDisplayPresetContext.Provider>
  )
}

export const useRuleDisplayPresetContext = () => useContext(RuleDisplayPresetContext)


